import React from "react";
import { Box, Typography, Tooltip } from "@mui/material";
import { summaryCardTheme as t } from "./styles";
import { getComplianceIcon } from "../../../assets/images/compliance/IconCompliance.jsx";

const formatCurrency = (value) =>
  typeof value === "number"
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
    : "$0.00";

const getTotalFailedControls = (overall) => {
  if (!overall || !Array.isArray(overall.compliance_by_framework)) return overall?.compliance_failed_controls_total ?? 0;
  return overall.compliance_by_framework.reduce(
    (sum, fw) => sum + (Number(fw.controls_failed_total) || 0),
    0
  );
};

const kpiConfig = (overall) => [
  {
    label: "Total ARNs",
    value: overall?.arns_total ?? 0,
    tooltip: "Total number of ARNs scanned across all services",
  },
  {
    label: "Security check Findings",
    value: overall?.arns_security_failed ?? 0,
    tooltip: "ARNs with at least one security (CSPM) finding",
    accent: "issue",
  },
  {
    label: "Control ID Findings",
    value: getTotalFailedControls(overall),
    tooltip: "Total failed compliance controls across all frameworks",
    accent: "warning",
  },
  {
    label: "Compliance Standards",
    value: overall?.compliance_frameworks_failed_count ?? null,
    tooltip: "Number of compliance frameworks with at least one failed control",
    emptyLabel: "—",
    frameworks: Array.isArray(overall?.compliance_frameworks_failed)
      ? overall.compliance_frameworks_failed
      : [],
  },
  {
    label: "Cloud Waste",
    value: overall?.arns_cloud_waste ?? 0,
    tooltip: "Total count of ARNs identified as Cloud Waste",
    accent: "success",
  },
  {
    label: "Cost (MTD)",
    value: overall?.cost_unblended_mtd_total ?? 0,
    formatter: formatCurrency,
    tooltip: "Unblended cost month-to-date across scanned ARNs",
    accent: "success",
  },
  {
    label: "Security+Compliance Finding",
    value: overall?.arns_both ?? 0,
    tooltip: "ARNs common to CSPM (security) and Compliance findings",
    accent: "issue",
  },
  {
    label: "Security+Compliance+Cost Findings",
    value: overall?.arns_all_three ?? 0,
    tooltip: "ARNs common to CSPM, Compliance and Cost Insight findings",
    accent: "issue",
  },
  {
    label: "Security+Compliance+Cost+Cloud Waste",
    value: overall?.arns_all_four ?? 0,
    tooltip: "ARNs common to CSPM, Compliance, Cost Insight and Cloud Waste",
    accent: "issue",
  },
];

function SummaryKpiCards({ overall, compact }) {
  const items = kpiConfig(overall);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, minmax(0, 1fr))",
          sm: "repeat(3, minmax(0, 1fr))",
          md: "repeat(4, minmax(0, 1fr))",
          // In compact (dashboard strip) view, show all KPIs in a single row on large screens.
          // In full dialog view, use multiple rows for better readability and responsiveness.
          lg: compact ? "repeat(9, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))",
          xl: compact ? "repeat(9, minmax(0, 1fr))" : "repeat(5, minmax(0, 1fr))",
        },
        gap: compact ? 1 : 2,
        fontFamily: t.fontFamily,
      }}
    >
      {items.map((item) => {
        const hasValue = item.value != null && item.value !== "" && (typeof item.value !== "number" || !Number.isNaN(item.value));
        const displayValue =
          item.emptyLabel != null && !hasValue
            ? item.emptyLabel
            : typeof item.formatter === "function"
            ? item.formatter(item.value)
            : item.value;
        const color =
          item.accent === "issue"
            ? t.colors.issue
            : item.accent === "warning"
            ? t.colors.warning
            : item.accent === "success"
            ? t.colors.success
            : t.colors.text;

        const tooltipContent =
          item.frameworks && item.frameworks.length > 0 ? (
            <Box sx={{ p: 1.5, minWidth: 220, maxWidth: 320 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontFamily: t.fontFamily,
                  fontWeight: 700,
                  mb: 0.25,
                  fontSize: "0.8rem",
                  color: "text.primary",
                }}
              >
                Compliance Standards
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: t.fontFamily,
                  fontSize: "0.72rem",
                  color: "text.secondary",
                  mb: 1,
                }}
              >
                Frameworks that currently have one or more failed controls:
              </Typography>
              <Box
                sx={{
                  maxHeight: 180,
                  overflowY: "auto",
                  pr: 0.5,
                }}
              >
                {item.frameworks.map((fw) => {
                  const iconSrc = getComplianceIcon(fw);
                  return (
                    <Box
                      key={fw}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 0.5,
                        gap: 1,
                      }}
                    >
                      {iconSrc && (
                        <Box
                          component="img"
                          src={iconSrc}
                          alt={fw}
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: 1,
                            objectFit: "contain",
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: t.fontFamily,
                          fontSize: "0.72rem",
                          color: "text.primary",
                        }}
                      >
                        {fw}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : (
            item.tooltip
          );

        return (
          <Tooltip
            key={item.label}
            title={tooltipContent}
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: "background.paper",
                  color: "text.primary",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: 3,
                  // Allow scrolling inside rich tooltip content (e.g. long framework lists)
                  pointerEvents: "auto",
                  "& .MuiTooltip-arrow": {
                    color: "background.paper",
                    "&::before": {
                      border: "1px solid",
                      borderColor: "divider",
                    },
                  },
                },
              },
            }}
          >
            <Box
              sx={{
                backgroundColor: compact ? "transparent" : t.colors.card,
                border: compact ? "none" : `1px solid ${t.colors.border}`,
                borderRadius: t.radius.lg,
                p: compact ? 0.5 : 2,
                boxShadow: compact ? "none" : t.shadow,
                transition: "border-color 0.15s ease, box-shadow 0.15s ease",
                "&:hover": compact ? {} : {
                  borderColor: t.colors.borderLight,
                  boxShadow:
                    "0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -2px rgba(0,0,0,0.06)",
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: t.colors.textMuted,
                  fontSize: compact ? "0.6rem" : "0.72rem",
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  mb: compact ? 0 : 0.5,
                  fontFamily: t.fontFamily,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.15,
                }}
              >
                {item.label}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color,
                  fontWeight: 700,
                  fontSize: compact ? "0.9375rem" : "1.25rem",
                  fontFamily: t.fontFamily,
                }}
              >
                {displayValue}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

export default SummaryKpiCards;
